/**
 * Menu card behaviour: gallery swap, size variants, add to cart.
 *
 * Add to cart goes through the theme's own cart contract rather than beside
 * it — the same CartLinesUpdateEvent the product form dispatches — so the cart
 * icon, the cart drawer and its auto-open all behave exactly as they do on a
 * product page. The fly-to-cart element is the theme's too.
 *
 * Everything is delegated from the document, so cards that arrive later
 * through section rendering, facet morphs or infinite scroll just work.
 */

import { CartLinesUpdateEvent } from '@shopify/events';
import '@theme/fly-to-cart';

/* ------------------------------------------------------------------ gallery */
document.addEventListener('click', (event) => {
  const thumb = event.target.closest('[data-menu-gallery] .menu-card__gthumb');
  if (!thumb) return;

  const card = thumb.closest('.menu-card');
  const main = card?.querySelector('[data-menu-main]');
  if (!main) return;

  for (const button of thumb.parentElement.querySelectorAll('.menu-card__gthumb')) {
    button.setAttribute('aria-current', String(button === thumb));
  }

  const nextSrc = thumb.dataset.menuShot;
  main.classList.add('is-swapping');

  const preload = new Image();
  preload.addEventListener('load', () => {
    main.src = nextSrc;
    main.removeAttribute('srcset');
    if (thumb.dataset.menuShotAlt) main.alt = thumb.dataset.menuShotAlt;
    main.classList.remove('is-swapping');
  });
  preload.addEventListener('error', () => main.classList.remove('is-swapping'));
  preload.src = nextSrc;
});

/* ----------------------------------------------------------------- variants */

/** Every variant of a card, with its option values, price and availability. */
function variantMap(card) {
  const node = card.querySelector('[data-menu-variant-map]');
  if (!node) return [];
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    console.warn('[menu-card] variant data could not be read', error);
    return [];
  }
}

/** The value currently chosen in each option row, in option order. */
function chosenValues(card) {
  return [...card.querySelectorAll('[data-menu-option]')].map(
    (row) => row.querySelector('.menu-card__size[aria-pressed="true"]')?.dataset.menuValue ?? null
  );
}

/**
 * Greys out values that cannot be reached from the current selection — picking
 * Iced should strike through a size that only exists hot, rather than letting
 * the shopper choose a combination that does not exist.
 */
function refreshAvailability(card, variants, chosen) {
  card.querySelectorAll('[data-menu-option]').forEach((row) => {
    const index = Number(row.dataset.menuOption);

    row.querySelectorAll('.menu-card__size').forEach((pill) => {
      const value = pill.dataset.menuValue;
      const reachable = variants.some(
        (variant) =>
          variant.available &&
          variant.options[index] === value &&
          chosen.every((picked, i) => i === index || picked === null || variant.options[i] === picked)
      );
      pill.disabled = !reachable;
    });
  });
}

function applyVariant(card, variant) {
  if (!variant) return;

  const idField = card.querySelector('[data-menu-variant-id]');
  if (idField) idField.value = variant.id;

  const form = card.querySelector('[data-menu-form]');
  if (form) form.dataset.menuPriceCents = String(variant.price);

  const amount = card.querySelector('[data-menu-amount]');
  if (amount) {
    // Replaced rather than rewritten so the price animation replays.
    const fresh = document.createElement('span');
    fresh.setAttribute('data-menu-amount', '');
    fresh.textContent = variant.priceText;
    amount.replaceWith(fresh);
  }
}

document.addEventListener('click', (event) => {
  const pill = event.target.closest('[data-menu-option] .menu-card__size');
  if (!pill || pill.disabled) return;

  const card = pill.closest('.menu-card');
  if (!card) return;

  for (const other of pill.parentElement.querySelectorAll('.menu-card__size')) {
    other.setAttribute('aria-pressed', String(other === pill));
  }

  const variants = variantMap(card);
  let chosen = chosenValues(card);

  // The exact combination, or the nearest sellable one if that pairing is gone.
  let match = variants.find(
    (variant) => variant.available && chosen.every((value, i) => value === null || variant.options[i] === value)
  );

  if (!match) {
    const index = Number(pill.parentElement.dataset.menuOption);
    match = variants.find((variant) => variant.available && variant.options[index] === pill.dataset.menuValue);

    if (match) {
      // Move the other rows onto the combination that does exist.
      card.querySelectorAll('[data-menu-option]').forEach((row) => {
        const i = Number(row.dataset.menuOption);
        row.querySelectorAll('.menu-card__size').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.menuValue === match.options[i]));
        });
      });
      chosen = chosenValues(card);
    }
  }

  applyVariant(card, match);
  refreshAvailability(card, variants, chosen);
});

/* ---------------------------------------------------------------- animation */

/**
 * Sends the card's picture arcing up to the cart icon, reusing the theme's
 * <fly-to-cart> element so the curve and timing match the product page.
 */
function flyToCart(card, button) {
  const cartIcon = document.querySelector('.header-actions__cart-icon');
  const image = card.querySelector('[data-menu-main]');
  const source = image?.currentSrc || image?.src;
  if (!cartIcon || !source) return;

  const flyer = document.createElement('fly-to-cart');
  flyer.classList.add('fly-to-cart--quick');
  flyer.style.setProperty('background-image', `url(${source})`);
  flyer.style.setProperty('--start-opacity', '0');
  flyer.source = button;
  flyer.destination = cartIcon;

  document.body.appendChild(flyer);
}

/** A short lift on the card itself, so the thing you pressed acknowledges it. */
function pulse(card) {
  card.classList.remove('is-added');
  void card.offsetWidth;
  card.classList.add('is-added');
  window.setTimeout(() => card.classList.remove('is-added'), 700);
}

let toastTimer;

/**
 * A small confirmation in the corner. Only used when the cart drawer is not
 * going to open by itself — two things announcing the same add is noise.
 */
function toast(card, title, variant) {
  document.querySelector('.menu-toast')?.remove();

  const image = card.querySelector('[data-menu-main]');
  const source = image?.currentSrc || image?.src || '';
  const root = window.Shopify?.routes?.root ?? '/';

  const el = document.createElement('div');
  el.className = 'menu-toast';
  el.setAttribute('role', 'status');

  const thumb = document.createElement('div');
  thumb.className = 'menu-toast__thumb';
  if (source) {
    const img = document.createElement('img');
    img.src = source;
    img.alt = '';
    thumb.appendChild(img);
  }

  const text = document.createElement('div');
  text.className = 'menu-toast__text';
  const heading = document.createElement('strong');
  heading.textContent = 'Added to cart';
  const detail = document.createElement('span');
  detail.textContent = variant && variant !== 'Default Title' ? `${title} · ${variant}` : title;
  text.append(heading, detail);

  const link = document.createElement('a');
  link.className = 'menu-toast__link';
  link.href = `${root}cart`;
  link.textContent = 'View cart';

  el.append(thumb, text, link);
  document.body.appendChild(el);

  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    el.classList.add('is-leaving');
    el.addEventListener('animationend', () => el.remove(), { once: true });
  }, 4000);
}

/** True when the theme is about to open the cart drawer on its own. */
function drawerWillOpen() {
  const drawer = document.querySelector('cart-drawer-component[auto-open]');
  if (!drawer) return false;
  return !drawer.closest('dialog')?.open;
}

/**
 * Holds the drawer shut for this one add.
 *
 * The drawer decides whether to open the instant our event is dispatched, by
 * reading its own auto-open attribute. So the attribute is lifted just before
 * the dispatch and put back straight after — the cart icon, the line items and
 * everything else still update, and opening from the cart icon is untouched.
 */
function suppressDrawer() {
  const drawers = [...document.querySelectorAll('cart-drawer-component[auto-open]')];
  if (!drawers.length) return () => {};

  for (const drawer of drawers) drawer.removeAttribute('auto-open');
  return () => {
    for (const drawer of drawers) drawer.setAttribute('auto-open', '');
  };
}

/* ----------------------------------------------------------------- add-ons */

/**
 * Builds the add-on chooser for a card and resolves with the lines to add,
 * or null when the shopper backs out.
 *
 * Quantities up to an add-on's `free` count cost nothing and travel as a note
 * on the tea's own line. Anything beyond that is added as its own cart line,
 * tagged with a hidden _parent so the cart can group it under its tea.
 */
function chooseAddons(card, addons, modifiers, baseCents) {
  return new Promise((resolve) => {
    const text = {
      heading: card.dataset.menuAddonHeading || 'Make it yours',
      note: card.dataset.menuAddonNote || '',
      confirm: card.dataset.menuAddonConfirm || 'Add to cart',
      skip: card.dataset.menuAddonSkip || 'No thanks',
      included: card.dataset.menuAddonIncluded || 'Included',
      modifiers: card.dataset.menuModifierHeading || 'Make it without',
      free: card.dataset.menuAddonFreeLabel || 'free',
      freeUsed: card.dataset.menuAddonFreeUsedLabel || 'free used',
    };

    const picked = new Map(addons.map((addon) => [addon.id, 0]));

    /**
     * "1 Honey & 1 add-in" means one of the honeys and one of the add-ins, not
     * one of each product. So the free allowance is pooled per group: an add-on
     * with no group keeps a private pool of its own.
     */
    const groupOf = (addon) => addon.group || `#${addon.id}`;

    const allowance = new Map();
    for (const addon of addons) {
      const key = groupOf(addon);
      allowance.set(key, Math.max(allowance.get(key) ?? 0, addon.free || 0));
    }

    /** How many of this add-on are free, given what the group has used already. */
    function freeFor(addon, used) {
      const key = groupOf(addon);
      const left = (allowance.get(key) ?? 0) - (used.get(key) ?? 0);
      const count = picked.get(addon.id) || 0;
      const free = Math.max(0, Math.min(count, left));
      used.set(key, (used.get(key) ?? 0) + free);
      return free;
    }

    /** Free and paid split for every add-on, in the order they are listed. */
    function split() {
      const used = new Map();
      return addons.map((addon) => {
        const count = picked.get(addon.id) || 0;
        const free = freeFor(addon, used);
        return { addon, count, free, paid: count - free };
      });
    }

    const dialog = document.createElement('dialog');
    dialog.className = 'menu-addons';
    dialog.innerHTML = `
      <form method="dialog" class="menu-addons__inner">
        <button class="menu-addons__close" type="submit" value="cancel" aria-label="Close"></button>
        <h2 class="menu-addons__heading"></h2>
        <p class="menu-addons__note"></p>
        <ul class="menu-addons__list"></ul>
        <div class="menu-addons__foot">
          <span class="menu-addons__total"><em>Total</em><strong data-total></strong></span>
          <button class="menu-addons__skip" type="submit" value="skip"></button>
          <button class="menu-addons__confirm" type="submit" value="confirm"></button>
        </div>
      </form>
    `;

    dialog.querySelector('.menu-addons__heading').textContent = text.heading;
    dialog.querySelector('.menu-addons__note').textContent = text.note;
    dialog.querySelector('.menu-addons__skip').textContent = text.skip;
    dialog.querySelector('.menu-addons__confirm').textContent = text.confirm;

    const list = dialog.querySelector('.menu-addons__list');
    const totalEl = dialog.querySelector('[data-total]');

    const money = (cents) => {
      const sample = addons.find((a) => a.priceText)?.priceText ?? '$0.00';
      const symbol = sample.replace(/[\d.,\s]/g, '');
      return `${symbol}${(cents / 100).toFixed(2)}`;
    };

    function repaint() {
      const parts = split();

      let cents = baseCents;
      for (const part of parts) cents += part.paid * part.addon.price;
      totalEl.textContent = money(cents);

      // "1 free" beside each group heading, counting down as it is used, so a
      // shopper can see the allowance exists and that it is theirs to place.
      for (const heading of list.querySelectorAll('.menu-addons__group[data-group]')) {
        const name = heading.dataset.group;
        const pool = allowance.get(name) ?? 0;
        const used = parts
          .filter((part) => groupOf(part.addon) === name)
          .reduce((sum, part) => sum + part.free, 0);
        const left = pool - used;
        const note = heading.querySelector('[data-free]');

        if (pool === 0) {
          note.textContent = '';
        } else if (left > 0) {
          note.textContent = `${left} ${text.free}`;
          note.classList.add('is-available');
        } else {
          note.textContent = text.freeUsed;
          note.classList.remove('is-available');
        }
      }

      for (const row of list.children) {
        if (!row.dataset.addon) continue; // group heading
        const part = parts.find((p) => String(p.addon.id) === row.dataset.addon);
        const { addon, count, free } = part;

        row.querySelector('[data-count]').textContent = count;
        row.querySelector('[data-minus]')?.toggleAttribute('disabled', count === 0);
        row.querySelector('[data-plus]')?.toggleAttribute(
          'disabled',
          addon.max > 0 && count >= addon.max
        );
        row.classList.toggle('is-on', count > 0);

        const priceEl = row.querySelector('[data-price]');
        if (free > 0 && free === count) {
          priceEl.textContent = text.included;
          priceEl.classList.add('is-free');
        } else if (free > 0) {
          priceEl.textContent = `${free} ${text.included.toLowerCase()}, then ${addon.priceText}`;
          priceEl.classList.remove('is-free');
        } else {
          priceEl.textContent = addon.priceText;
          priceEl.classList.remove('is-free');
        }
      }
    }

    let lastGroup = null;
    for (const addon of addons) {
      if (addon.group && addon.group !== lastGroup) {
        const heading = document.createElement('li');
        heading.className = 'menu-addons__group';
        heading.dataset.group = addon.group;
        heading.innerHTML = '<span></span><em data-free></em>';
        heading.querySelector('span').textContent = addon.group;
        list.appendChild(heading);
        lastGroup = addon.group;
      }

      const row = document.createElement('li');
      row.className = 'menu-addons__row';
      row.dataset.addon = String(addon.id);
      row.innerHTML = `
        <div class="menu-addons__thumb">${addon.image ? `<img src="${addon.image}" alt="">` : ''}</div>
        <div class="menu-addons__info">
          <strong></strong>
          <span data-price></span>
        </div>
        ${
          addon.mode === 'single'
            ? '<span class="menu-addons__tick" data-tick aria-hidden="true"></span><span hidden data-count>0</span>'
            : `<div class="menu-addons__step">
                 <button type="button" data-minus aria-label="One fewer">&minus;</button>
                 <span data-count>0</span>
                 <button type="button" data-plus aria-label="One more">+</button>
               </div>`
        }
      `;
      // Just the name. The allowance is announced once on the group heading,
      // so repeating it on every row said "1 included" nine times over.
      row.querySelector('strong').textContent = addon.title;

      if (addon.mode === 'single') {
        // Pick one from the group: choosing Oat clears Almond.
        row.classList.add('menu-addons__row--single');
        row.addEventListener('click', (event) => {
          if (event.target.closest('button')) return;
          const on = (picked.get(addon.id) || 0) > 0;
          for (const other of addons) {
            if (groupOf(other) === groupOf(addon)) picked.set(other.id, 0);
          }
          picked.set(addon.id, on ? 0 : 1);
          repaint();
        });
      } else {
        row.querySelector('[data-minus]').addEventListener('click', () => {
          picked.set(addon.id, Math.max(0, (picked.get(addon.id) || 0) - 1));
          repaint();
        });
        row.querySelector('[data-plus]').addEventListener('click', () => {
          const next = (picked.get(addon.id) || 0) + 1;
          picked.set(addon.id, addon.max > 0 ? Math.min(next, addon.max) : next);
          repaint();
        });
      }

      if (addon.available === false) row.classList.add('is-unavailable');
      list.appendChild(row);
    }

    // Free modifiers: no price, no product, just a tick that becomes a note on
    // the line. "No egg" has to reach the kitchen, not the invoice.
    const removed = new Set();

    if (modifiers.length) {
      const heading = document.createElement('li');
      heading.className = 'menu-addons__group';
      heading.textContent = text.modifiers;
      list.appendChild(heading);

      for (const label of modifiers) {
        const row = document.createElement('li');
        row.className = 'menu-addons__row menu-addons__row--single menu-addons__row--modifier';
        row.innerHTML = `
          <div class="menu-addons__info"><strong></strong></div>
          <span class="menu-addons__tick" aria-hidden="true"></span>
        `;
        row.querySelector('strong').textContent = label;
        row.setAttribute('role', 'checkbox');
        row.setAttribute('aria-checked', 'false');
        row.tabIndex = 0;

        const toggle = () => {
          const on = removed.has(label);
          if (on) {
            removed.delete(label);
          } else {
            removed.add(label);
          }
          row.classList.toggle('is-on', !on);
          row.setAttribute('aria-checked', String(!on));
        };

        row.addEventListener('click', toggle);
        row.addEventListener('keydown', (event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            toggle();
          }
        });

        list.appendChild(row);
      }
    }

    // Nothing preselected by default: with nine herbs to choose from, picking
    // one for the shopper reads as a decision already made. The heading says
    // the free one is there. Turn the setting on to claim it automatically.
    if (card.dataset.menuAddonPreselect === 'true') {
      const claimed = new Set();
      for (const addon of addons) {
        const key = groupOf(addon);
        const pool = allowance.get(key) ?? 0;
        if (pool > 0 && !claimed.has(key)) {
          picked.set(addon.id, pool);
          claimed.add(key);
        }
      }
    }
    repaint();

    dialog.addEventListener('close', () => {
      const answer = dialog.returnValue;
      dialog.remove();

      if (answer === 'cancel') {
        resolve(null);
        return;
      }

      if (answer === 'skip') {
        resolve({ extras: [], notes: {} });
        return;
      }

      const notesFromModifiers = {};
      for (const label of removed) notesFromModifiers[label] = 'Yes';

      const extras = [];
      const notes = {};
      for (const { addon, count, free, paid } of split()) {
        if (count === 0) continue;
        if (free > 0) notes[addon.title] = `${free} ${text.included.toLowerCase()}`;
        if (paid > 0) extras.push({ id: addon.id, quantity: paid });
      }
      resolve({ extras, notes: { ...notesFromModifiers, ...notes } });
    });

    document.body.appendChild(dialog);
    dialog.showModal();
  });
}

/* -------------------------------------------------------------- add to cart */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-menu-form]');
  if (!form) return;

  event.preventDefault();

  const card = form.closest('.menu-card');
  const button = form.querySelector('[data-menu-add]');
  const label = form.querySelector('[data-menu-add-text]');
  if (!button || button.disabled) return;

  const formData = new FormData(form);
  const variantId = String(formData.get('id'));
  const quantity = Number(formData.get('quantity')) || 1;

  /* --- the add-on chooser, for products that have one --- */
  let extras = [];
  let notes = {};

  if (button.dataset.menuHasAddons === 'true' && card) {
    let addons = [];
    const addonNode = card.querySelector('[data-menu-addons]');
    if (addonNode) {
      try {
        addons = JSON.parse(addonNode.textContent);
      } catch (error) {
        console.warn('[menu-card] add-on data could not be read', error);
      }
    }

    let modifiers = [];
    const modifierNode = card.querySelector('[data-menu-modifiers]');
    if (modifierNode) {
      try {
        modifiers = JSON.parse(modifierNode.textContent);
      } catch (error) {
        console.warn('[menu-card] modifier data could not be read', error);
      }
    }

    if (addons.length || modifiers.length) {
      const basePrice = Number(form.dataset.menuPriceCents ?? 0);
      const choice = await chooseAddons(card, addons, modifiers, basePrice);
      if (!choice) return; // backed out, nothing added
      extras = choice.extras;
      notes = choice.notes;
    }
  }

  // Ask for the cart sections back in the same request, so the drawer has
  // fresh markup to morph in the moment it opens.
  const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((node) => node.dataset.sectionId)
    .filter(Boolean);

  const parentTitle = button.dataset.menuProductTitle || '';
  const items = [
    { id: variantId, quantity, properties: notes },
    ...extras.map((extra) => ({
      ...extra,
      properties: parentTitle ? { _parent: parentTitle } : {},
    })),
  ];

  const payload = { items };
  if (sectionIds.length) payload.sections = sectionIds.join(',');

  button.classList.add('is-busy');

  if (card && card.dataset.menuFly === 'true') {
    flyToCart(card, button);
  }

  const keepShut = card?.dataset.menuOpenDrawer === 'true' ? null : suppressDrawer();

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  keepShut?.();

  try {
    const line = await fetch(`${form.action}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then((response) => response.json());

    button.classList.remove('is-busy');

    if (line.status) {
      if (label) label.textContent = line.description || line.message;
      deferred.resolve({ cart: null, detail: { didError: true } });
      window.setTimeout(restore, 2600);
      return;
    }

    confirmAdded();
    if (card) pulse(card);

    const cart = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    }).then((response) => response.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'menu-card',
        itemCount: quantity,
        sections: line.sections,
        didError: false,
      },
    });

    const drawerOpening = card?.dataset.menuOpenDrawer === 'true' && drawerWillOpen();
    if (card && card.dataset.menuToast === 'true' && !drawerOpening) {
      const first = line.items?.[0] ?? line;
      toast(card, first.product_title, first.variant_title);
    }
  } catch (error) {
    button.classList.remove('is-busy');
    deferred.reject(error);
    form.submit(); // network trouble: hand it back to the browser
  }

  function confirmAdded() {
    button.classList.add('is-added');
    if (label) label.textContent = 'Added';
    window.setTimeout(restore, 1800);
  }

  function restore() {
    button.classList.remove('is-added');
    if (label) label.textContent = button.dataset.menuAddLabel;
  }
});